import { EntityAttribute } from "shared/api/api";
import {
  VEHICLE_ACCESSOR,
  VEHICLE_LAST_KNOWN_DEALER_ACCESSOR,
} from "shared/schemas/constants";
import {
  GetDisplayLabelFunction,
  GetSchemaEntryFunction,
  SchemaEntryOverride,
  SchemaRelations,
  SortSchemaItem,
  UseSchema,
} from "shared/schemas/types";
import { DataType } from "shared/types";
import { getAttributeLabel } from "shared/utils/string-utils";

import { FilterSchemaItem, FilterType } from "features/ui/Filters/types";
import { SchemaEntry } from "features/ui/MaterialTable";
import { SelectOption } from "features/ui/Select";

const getFilterTypeFromAPIType = (apiType: string): FilterType => {
  switch (apiType) {
    case "number<double>":
    case "integer<int32>":
    case "integer<int64>":
      return FilterType.NUMBER;
    case "string<duration>":
      return FilterType.DURATION;
    case "string<date>":
    case "string<date-time>":
    case "string<date-time-utc>":
    case "string<date-time-no-tz>":
      return FilterType.DATE;
    case "array[relation]":
      return FilterType.EXISTS;
    case "boolean":
      return FilterType.BOOLEAN;
    default:
      return FilterType.STRING;
  }
};

export const getDisplayLabelFunction =
  (attributeMap?: Record<string, EntityAttribute>): GetDisplayLabelFunction =>
  (accessor: string, defaultLabel: string, suffix?: string): string => {
    if (!attributeMap || !attributeMap[accessor]) {
      return defaultLabel;
    }

    if (suffix) {
      return `${attributeMap[accessor].displayName} ${suffix}`;
    }

    return attributeMap[accessor].displayName;
  };

export const getAttributeMap = (attributes?: EntityAttribute[]) =>
  attributes?.reduce((map: Record<string, EntityAttribute>, obj) => {
    map[obj.ID] = obj;

    return map;
  }, {});

export const getFilterFunction =
  (
    filterType: (item: FilterSchemaItem) => FilterSchemaItem,
    attributeMap?: Record<string, EntityAttribute>,
    valuesEndpoint?: string
  ) =>
  (
    accessor: string,
    filterOverride: Partial<FilterSchemaItem> = {},
    fieldNamePrefix?: string,
    labelPrefix?: string
  ) => {
    if (!attributeMap || !attributeMap[accessor]) {
      return undefined;
    }

    const attributeConfig = attributeMap[accessor];
    if (!attributeConfig.filtering) {
      return undefined;
    }

    const label = labelPrefix
      ? `${labelPrefix} > ${attributeConfig.displayName}`
      : attributeConfig.displayName;

    const fieldName = fieldNamePrefix
      ? `${fieldNamePrefix}.${accessor}`
      : accessor;

    return filterType({
      label,
      fieldName,
      fieldNameForAPI: accessor,
      valuesEndpoint,
      relationEndpoint: attributeConfig.relationEndpoint || undefined,
      ...getFilterPropertiesFromAttribute(attributeConfig),
      ...filterOverride,
    });
  };

export const getFilterPropertiesFromAttribute = (
  attributeConfig: EntityAttribute
) => ({
  description: attributeConfig.description || undefined,
  search: attributeConfig.values,
  filterType: getFilterTypeFromAPIType(attributeConfig.type),
  filterDataType: getSchemaType(attributeConfig.type),
  disableSelectFilters: !attributeConfig.values,
  disableContainsFilters: !attributeConfig.filteringConfig.contains,
  disableStartsWithFilters: !attributeConfig.filteringConfig.startsWith,
  onlyAllowPositiveIntegers:
    !attributeConfig.filteringConfig.negativeNumbers &&
    !attributeConfig.filteringConfig.decimalNumbers,
  disableIsEmptyFilters: !attributeConfig.filteringConfig.empty,
  enableMinMaxFilters: attributeConfig.filteringConfig.minMax,
  loadDataOnOpen: attributeConfig.filteringConfig.lowCardinality,
});

export const getSchemaEntryFunction =
  (
    getFilter: (
      accessor: string,
      filterOverride?: Partial<FilterSchemaItem>,
      fieldNamePrefix?: string,
      labelPrefix?: string
    ) => FilterSchemaItem | undefined,
    attributeMap?: Record<string, EntityAttribute>
  ) =>
  (
    accessor: string,
    configOverride: Partial<SchemaEntry> = {},
    filterOverride: Partial<FilterSchemaItem> = {},
    sortOverride: SortSchemaItem = {},
    parentEntityAttribute?: EntityAttribute
  ): SchemaEntry | undefined => {
    if (!attributeMap || !attributeMap[accessor]) {
      return undefined;
    }

    const attributeConfig = attributeMap[accessor];

    const label = parentEntityAttribute?.displayName
      ? `${parentEntityAttribute?.displayName} > ${attributeConfig.displayName}`
      : attributeConfig.displayName;

    const accessorToUse = parentEntityAttribute?.ID
      ? `${parentEntityAttribute?.ID}.${accessor}`
      : accessor;

    return {
      ...{
        label,
        description: attributeConfig.description || undefined,
        accessor: accessorToUse,
        dataType: getSchemaType(attributeConfig.type),
        sortable: attributeConfig.sorting,
        limitedWidthClass: attributeConfig.displayWideColumn
          ? "w-80"
          : undefined,
        filter: getFilter(
          accessor,
          filterOverride,
          parentEntityAttribute?.ID,
          parentEntityAttribute?.displayName
        ),
        sort: {
          fieldNameForAPI: sortOverride?.fieldNameForAPI || accessorToUse,
        },
        hideInTable: attributeConfig.hideInTable,
        hideFilter: attributeConfig.hideFilter,
        byVehicleAgeBirthday: attributeConfig.byVehicleAgeBirthday,
        byVehicleAgeExposure: attributeConfig.byVehicleAgeExposure,
        byVehicleAgeExposureBuckets:
          attributeConfig.byVehicleAgeExposureBuckets,

        columnWidth: attributeConfig.columnWidth,
        ...(attributeConfig.format && { format: attributeConfig.format }),
      },
      ...configOverride,
    };
  };

export const getSchemaEntryLabel = (
  entries: SchemaEntry[],
  accessor: string
): string => {
  const entry = entries.find(
    (schemaEntry) => schemaEntry.accessor === accessor
  );

  return entry && typeof entry.label === "string"
    ? entry.label
    : getAttributeLabel(accessor);
};

export interface ChartAxisField {
  schema?: SchemaEntry[];
  field?: string;
  // Set instead of `field` when the axis runs over a date on a related schema;
  // both halves are needed because the relation's own entry holds the child
  // schema.
  relationParentAttribute?: string;
  relationChildAttribute?: string;
}

// Names the attribute a trend chart's x-axis runs over. A date on a related
// schema reads "<Relation> - <Field>", the same shape the dashboard filter
// summary above the chart uses for its nested date filters.
export const getChartAxisLabel = ({
  schema,
  field,
  relationParentAttribute,
  relationChildAttribute,
}: ChartAxisField): string | undefined => {
  if (!schema?.length) return undefined;

  if (relationParentAttribute && relationChildAttribute) {
    const relationEntries =
      schema.find((entry) => entry.accessor === relationParentAttribute)
        ?.relationSchema ?? [];

    return `${getSchemaEntryLabel(schema, relationParentAttribute)} - ${getSchemaEntryLabel(relationEntries, relationChildAttribute)}`;
  }

  return field ? getSchemaEntryLabel(schema, field) : undefined;
};

export const removeFirstLabelPart = (label: string): string => {
  const parts = label.split(" > ");

  return parts.length > 1 ? parts.slice(1).join(" > ") : label;
};

export const getSchemaForAccessorFunction =
  (schemaItems: SchemaEntry[]) =>
  (accessor: string, useShortLabel?: boolean) => {
    const schemaEntry = schemaItems.find(
      (schemaEntry) => schemaEntry && schemaEntry.accessor === accessor
    );

    if (!schemaEntry || !useShortLabel) {
      return schemaEntry;
    }

    // e.g. from "Vehicle > Mileage" to "Mileage"
    return {
      ...schemaEntry,
      label: removeFirstLabelPart(schemaEntry.label),
    };
  };

export const getFilterableSortableAccessors = (
  schema: SchemaEntry[]
): { filterableAttributes: string[]; sortableAttributes: string[] } => ({
  filterableAttributes: schema
    .filter((entry) => entry.filter && !entry.hideFilter)
    .map((entry) => entry.filter?.fieldName ?? entry.accessor),
  sortableAttributes: schema
    .filter((entry) => entry.sortable)
    .map((entry) => entry.accessor),
});

export const getSchemaType = (type: string | undefined): DataType => {
  if (type && (type.includes("int") || type.includes("double"))) {
    return DataType.NUMBER;
  }

  if (type === "string<date>") return DataType.DATE;

  if (type === "string<date-time>") return DataType.DATE_WITH_TIME;

  if (type === "string<date-time-no-tz>") return DataType.DATE_WITH_TIME_NO_TZ;

  if (type === "string<date-time-utc>") return DataType.DATE_WITH_TIME_UTC;

  if (type === "array[object]") return DataType.OBJECT;

  if (type === "boolean") return DataType.BOOLEAN;

  return DataType.STRING;
};

export const getVehicleDisplayNameValue = (displayName: string) =>
  `Vehicle: ${displayName}`;

export const getVehicleIdValue = (ID: string) => `${VEHICLE_ACCESSOR}.${ID}`;

export const formatVehicleAttribute = ({
  ID,
  displayName,
  ...otherAttrs
}: EntityAttribute) => ({
  ID: getVehicleIdValue(ID),
  displayName: getVehicleDisplayNameValue(displayName),
  ...otherAttrs,
});

export const formatVehicleEntityToSelectOption = ({
  ID,
  displayName,
}: EntityAttribute): SelectOption => ({
  id: getVehicleIdValue(ID),
  value: getVehicleDisplayNameValue(displayName),
});

export const getVehicleLastKnownDealerDisplayNameValue = (
  displayName: string,
  vehicleDealerAttribute?: EntityAttribute
) => `Vehicle: ${vehicleDealerAttribute?.displayName} ${displayName}`;

export const getVehicleLastKnownDealerIdValue = (ID: string) =>
  `${VEHICLE_ACCESSOR}.${VEHICLE_LAST_KNOWN_DEALER_ACCESSOR}.${ID}`;

export const filterGroupableAttributes = ({
  grouping,
  type,
}: EntityAttribute) => grouping && type !== "relation";

export const sortByValue = (a: SelectOption, b: SelectOption) =>
  b.value > a.value ? -1 : 1;

export const sortByDisplayName = (a: EntityAttribute, b: EntityAttribute) =>
  b.displayName > a.displayName ? -1 : 1;

export const getSchemaFromAttributes = (
  attributes: EntityAttribute[] = [],
  getSchemaEntry: GetSchemaEntryFunction,
  overrides: SchemaEntryOverride = {},
  relations: SchemaRelations = {},
  accessorsToHide: string[] = [],
  relationshipDisplayOnly = false
) => {
  const schema: SchemaEntry[] = [];

  attributes.forEach((attribute) => {
    if (
      attribute.relationEndpoint &&
      relations[attribute.relationEndpoint] &&
      !accessorsToHide.includes(attribute.ID) &&
      attribute.type === "relation"
    ) {
      const {
        attributes: relationAttributes,
        getSchemaEntry: getRelationshipSchemaEntry,
        schema: relationSchema,
      } = relations[attribute.relationEndpoint];

      if (relationAttributes) {
        // if overrides include a key starting with attribute.ID + ".", then it's a relation override
        // and we should pass the nested key/attribute (everything AFTER dot) to the getRelationshipSchemaEntry function
        const relationOverrides = Object.keys(overrides).reduce(
          (acc: SchemaEntryOverride, key: string) => {
            if (key.startsWith(`${attribute.ID}.`)) {
              const nestedKey = key.split(".")[1];
              acc[nestedKey] = overrides[key];
            }

            return acc;
          },
          {}
        );

        schema.push(
          ...relationAttributes
            .filter((relationAttribute) =>
              relationSchema.find(
                (schemaEntry) => schemaEntry.accessor === relationAttribute.ID
              )
            )
            .map((entry: EntityAttribute) => {
              const schemaOverrides = {
                ...relationOverrides[entry.ID]?.schemaOverride,
              };
              // we sometimes only want to display the relationship data, but not enable sort/filter as we have that in
              // separate functionality like Vehicles Filter. We can pass this as a relationshipDisplayOnly prop to the function
              if (relationshipDisplayOnly) {
                schemaOverrides.filter = undefined;
                schemaOverrides.sortable = false;
              }

              if (attribute.hideFilter) {
                schemaOverrides.hideFilter = true;
              }

              if (attribute.hideInTable) {
                schemaOverrides.hideInTable = true;
              }

              // if Relationship object does not have sorting/filtering enabled, we should disable it for all it's attributes as well
              if (!attribute.sorting) {
                schemaOverrides.sortable = false;
              }

              if (!attribute.filtering) {
                schemaOverrides.filter = undefined;
              }

              const filterOverrides = {
                disableFiltering: true,
                ...relationOverrides[entry.ID]?.filterOverride,
              };
              const sortOverrides = {
                ...relationOverrides[entry.ID]?.sortOverride,
              };

              return getRelationshipSchemaEntry!(
                entry.ID,
                schemaOverrides,
                filterOverrides,
                sortOverrides,
                attribute
              ) as SchemaEntry;
            })
        );
      }
    } else if (!accessorsToHide.includes(attribute.ID)) {
      const {
        schemaOverride,
        filterOverride,
        sortOverride,
        parentEntityAttribute,
        ignore,
      } = overrides[attribute.ID] || {};

      const schemaEntry = getSchemaEntry(
        attribute.ID,
        schemaOverride,
        filterOverride,
        sortOverride,
        parentEntityAttribute
      );

      if (schemaEntry && !ignore) {
        schema.push(schemaEntry);
      }
    }
  });

  return schema;
};

// extracts string after the last dot to get the base attribute name
export const getMainAttributeName = (fullAttributeName: string) =>
  fullAttributeName.substring(fullAttributeName.lastIndexOf(".") + 1);

/**
 * Builds the relation-endpoint key for a schema-definition (v2) relation. The
 * result is matched against the backend `attribute.relationEndpoint` field, so
 * the `/api/v2/…` format must stay in lockstep with what the API emits.
 */
export const buildSchemaRelationEndpoint = (
  tableType: string,
  name: string
): string => `/api/v2/${tableType}/${name}`;

/**
 * Builds the values-endpoint routing sentinel for a schema-definition (v2)
 * relation. The bare `v2/…` prefix (no leading slash) is a marker that
 * `genericValuesCallback` strips before routing to `clientV2` — it is not a
 * request path. Kept beside buildSchemaRelationEndpoint so both endpoint
 * formats live in one place.
 */
export const buildSchemaValuesEndpoint = (
  tableType: string,
  name: string
): string => `v2/${tableType}/${name}`;

/**
 * Inverse of buildSchemaRelationEndpoint: decodes a schema-definition attribute
 * `endpoint` (`/api/v{n}/{tableType}/{name}`) back into its parts. Reads from
 * the tail so an added/dropped leading segment (e.g. the `/api` prefix) doesn't
 * shift the result. Kept beside the builders so the encode/decode pair stays in
 * sync if the trailing `{tableType}/{name}` shape ever changes.
 */
export const getSchemaEndpointParts = (
  endpoint: string
): { tableType?: string; schemaName?: string } => {
  const [tableType, schemaName] = endpoint.split("/").slice(-2);

  return { tableType, schemaName };
};

/**
 * This allows us to grab the values for m2m relationships, like vehicle ECUs and vehicle options.
 * For example: /api/v1/vehicleECUs/values/ECUID will get us relevant ECU IDs that we need. ECU attributes are
 * retrieved using getGroupBySelectOptions() as a one-to-many relationship in relevant schemas, ie. vehiclesSchema.
 *
 * v1-only contract: the sole caller fetches the result through the v1 `client`, so every group-by
 * relation endpoint must be v1. The prefix strip is version-agnostic purely so a stray v2 endpoint
 * degrades to a predictable (still-wrong) v1 path rather than a malformed `/api/v2/…` one — it is not
 * v2 support. Add v2 routing here (via clientV2) before wiring up any v2 group-by relation.
 */
export const constructValuesEndpoint = (endpoint: string, IDColumn: string) =>
  endpoint.replace(/^\/api\/v\d+\//, "") + "/values/" + IDColumn;

/**
 * Helper function to determine if the attribute is a one-to-many relationship
 * @param attribute EntityAttribute
 * @returns boolean
 */
export const hasOneToManyRelation = ({
  type,
  relationEndpoint,
  grouping,
  relationEndpointIDColumn,
}: EntityAttribute): Boolean =>
  Boolean(
    ["relation", "array[relation]"].includes(type) &&
    relationEndpoint &&
    grouping &&
    !relationEndpointIDColumn
  );

export const sortAndFilterSchemaByWhitelistedAccessors = (
  schema: SchemaEntry[],
  orderedWhitelistedAccessors?: string[]
) => {
  if (!orderedWhitelistedAccessors) {
    return schema;
  }

  return schema
    .filter(({ accessor }) => orderedWhitelistedAccessors.includes(accessor))
    .sort((a, b) => {
      const aIndex = orderedWhitelistedAccessors.indexOf(a.accessor);
      const bIndex = orderedWhitelistedAccessors.indexOf(b.accessor);

      return aIndex - bIndex;
    });
};

/**
 * Creates a minimal schema for an entity.
 * This is used when one schema needs to reference another schema that would
 * otherwise create a circular dependency (e.g., repairs referencing work orders,
 * work orders referencing repairs).
 *
 * @param attributes - The entity attributes to build the schema from
 * @param genericFilter - The generic filter function for this entity type
 * @param overrides - Schema entry overrides to apply
 * @param relations - Related schemas to include
 * @param valuesEndpoint - Part of the route to values endpoint
 * @returns A minimal UseSchema object
 */
export const createMinimalSchema = (
  attributes: EntityAttribute[] | undefined,
  genericFilter: (item: FilterSchemaItem) => FilterSchemaItem,
  overrides: SchemaEntryOverride = {},
  relations: SchemaRelations = {},
  valuesEndpoint?: string
): UseSchema => {
  const attributeMap = getAttributeMap(attributes);
  const getDisplayLabel = getDisplayLabelFunction(attributeMap);
  const getFilter = getFilterFunction(
    genericFilter,
    attributeMap,
    valuesEndpoint
  );
  const getSchemaEntry = getSchemaEntryFunction(getFilter, attributeMap);

  const minimalSchema: UseSchema = {
    schema: getSchemaFromAttributes(
      attributes,
      getSchemaEntry,
      overrides,
      relations,
      []
    ),
    getDisplayLabel,
    attributeMap,
    attributes,
    attributeAccessors: [],
    getSchemaEntry,
    getSchemaForAccessor: getSchemaForAccessorFunction([]),
  };

  minimalSchema.attributeAccessors = minimalSchema.schema.map(
    ({ accessor, filter }) => filter?.fieldName || accessor
  );
  minimalSchema.getSchemaForAccessor = getSchemaForAccessorFunction(
    minimalSchema.schema
  );

  return minimalSchema;
};
